import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { api, tokenStore } from '../api/client.js';

interface AuthState {
  isAuthenticated: boolean;
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  /** Adopt tokens minted elsewhere (e.g. arriving from the OAuth callback). */
  setSession: (accessToken: string, refreshToken: string) => void;
  logout: () => Promise<void>;
}

interface RegisterInput {
  email: string;
  password: string;
  fullName?: string;
  organizationName: string;
  /** Present only when this deployment guards the sign-up form. */
  recaptchaToken?: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!tokenStore.access);

  const login = useCallback(async (email: string, password: string, recaptchaToken?: string) => {
    const res = await api.post('/v1/auth/login', { email, password, recaptchaToken });
    tokenStore.set(res.data.accessToken, res.data.refreshToken);
    setIsAuthenticated(true);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const res = await api.post('/v1/auth/register', input);
    tokenStore.set(res.data.tokens.accessToken, res.data.tokens.refreshToken);
    setIsAuthenticated(true);
  }, []);

  const setSession = useCallback((accessToken: string, refreshToken: string) => {
    tokenStore.set(accessToken, refreshToken);
    setIsAuthenticated(true);
  }, []);

  /**
   * Signs out here, and — on an SSO connection with single logout — at the
   * identity provider too.
   *
   * The two calls are awaited before the tokens are dropped, because the SLO
   * call needs the access token it is about to invalidate. Everything after
   * that happens whatever the server said: a logout that leaves someone
   * signed in because a request failed is the one outcome it may not have.
   *
   * Without the redirect, signing out and signing back in costs no prompt at
   * all — the IdP still has its own session — which looks exactly like the
   * logout never happened. On a shared machine that is the whole problem.
   */
  const logout = useCallback(async () => {
    const refresh = tokenStore.refresh;
    let idpLogout: string | undefined;
    try {
      const res = await api.post('/v1/auth/sso/logout', {});
      idpLogout = res.data?.redirectUrl;
    } catch {
      /* not an SSO session, or the provider is unreachable */
    }
    if (refresh) {
      await api.post('/v1/auth/logout', { refreshToken: refresh }).catch(() => {});
    }
    tokenStore.clear();
    setIsAuthenticated(false);
    if (idpLogout) window.location.assign(idpLogout);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, register, setSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
